import React, { useState, useEffect, useMemo } from "react";

import { Box, Button, Divider, Typography } from "@mui/material";
import Toast from "../../../utils/Toast";
import { usePortal } from "../../../context/PortalsContext";
import { useAuth } from "../../../context/AuthContext";
import { useLocation, useParams } from "react-router-dom";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  GetPortalGroupParams,
  GetAllPortalsPaginatedResult,
  GetAllGroupsResult,
  GroupResult,
  portalGroupBody,
} from "../../../services/api/types/portalGroup/portalGroup";
import {
  getPortalGroups,
  bulkDeletePortalGroups,
  createUpdatePortalGroup,
} from "../../../services/api/apiClient";
import { brand } from "../../../theme/themePrimitives";
import { DeleteGroupModal } from "./modal/DeleteGroupModal";
import { ReplaceGroupModal } from "./modal/ReplaceGroupModal";
import { AddNewGroupModal } from "./modal/AddNewGroupModal";
import { EditNewGroupModal } from "./modal/EditGroupModal";
import { DataTable } from "../../../components/DataTable/DataTable";
import { REMOVETEXT } from "../../../utils/constants";
import { useToast } from "../../../context/ToastContext";
import { extractErrorMessage } from "../../../utils/errorUtils";
import { CustomActionIconLink } from "../../../components/CustomActionIconLink/CustomActionIconLink";

interface PortalGroupsProps {}
export const PortalGroups: React.FC<PortalGroupsProps> = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [portalGroupResponse, setPortalGroupResponse] =
    useState<GetAllPortalsPaginatedResult>();
  const [portalGroupsArray, setPortalGroupsArray] = useState<
    GetAllGroupsResult[]
  >([]);
  const [nestedPortalGroupsArray, setNestedPortalGroupsArray] = useState<
    GetAllGroupsResult[]
  >([]);
  const [onlyParentsGroupsArray, setOnlyParentsGroupsArray] = useState<
    GetAllGroupsResult[]
  >([]);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteGroupModal, setDeleteGroupModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [openAddNewGroupModal, setOpenAddNewGroupModal] = useState(false);
  const [openEditGroupModal, setOpenEditGroupModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [replacePortalDropdownNameError, setReplacePortalDropdownNameError] =
    useState(false);
  const [selectedCourseModal, setSelectedPortalModal] = useState<
    GetAllGroupsResult | GroupResult
  >();
  const [thumbnailURL, setThumbnailUrl] = useState<string | null>("");
  const [parentPortalGroupID, setParentPortalGroupID] = useState<string | null>(
    null,
  );
  const [selectedPortalGroup, setSelectedPortalGroup] = useState<
    GetAllGroupsResult[]
  >([]);

  const selectedGroupIds = selectedPortalGroup.map(
    (group) => group.PortalGroupUid,
  );
  const anySelected = selectedPortalGroup.length > 0;
  const [buttonLoading, setButtonLoading] = useState(false);
  const { portals } = usePortal();
  const { auth } = useAuth();
  const { portalUid } = useParams<{ portalUid: string }>();
  const location = useLocation();
  const { showToast } = useToast();

  const fetchPortalGroups = async (
    params: GetPortalGroupParams = {},
  ): Promise<any> => {
    try {
      setLoading(true);
      if (!portals.selectedPortal) {
        return;
      }

      const response: GetAllPortalsPaginatedResult = await getPortalGroups(
        portals.selectedPortal.portalUid,
        params,
      );

      return response;
    } catch (error: any) {
      console.log("Error:", error);

      showToast(
        extractErrorMessage(error, "Failed to fetch portal groups"),
        "error",
      );
      setErrorMessage(
        extractErrorMessage(error, "Failed to fetch portal groups"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadPaginatedGroups = async (params: GetPortalGroupParams = {}) => {
    const URLParams = new URLSearchParams(window.location.search);
    const paramLimit = URLParams.get("limit");

    params.limit = paramLimit ? parseInt(paramLimit, 10) : limit;
    if (searchQuery) {
      params.name = searchQuery;
    }
    const paginagedGroups = await fetchPortalGroups(params);
    if (paginagedGroups && "rows" in paginagedGroups) {
      setPortalGroupResponse(paginagedGroups);
      setPortalGroupsArray(paginagedGroups.rows);
    }
  };

  const loadShortGroups = async () => {
    const allGroups = await fetchPortalGroups({ short: true });
    if (Array.isArray(allGroups)) {
      setNestedPortalGroupsArray(allGroups);
    }
  };

  const loadParentGroups = async () => {
    const parentGroupsOnly = await fetchPortalGroups({ onlyParents: true });
    if (Array.isArray(parentGroupsOnly)) {
      setOnlyParentsGroupsArray(parentGroupsOnly);
    }
  };

  const handleBulkDeleteClick = async () => {
    try {
      if (
        !portals.selectedPortal ||
        !auth.user?.UserPortalUid ||
        !selectedGroupIds.length
      ) {
        return;
      }

      const response = await bulkDeletePortalGroups(
        portals.selectedPortal.portalUid,
        {
          groupIds: selectedGroupIds,
          validate: true,
        },
      );

      const canDeleteDirectly = response === true || response?.data === true;

      if (canDeleteDirectly) {
        setShowReplaceModal(false);
        setParentPortalGroupID(null);
        setReplacePortalDropdownNameError(false);
        setDeleteGroupModal(true);
      } else {
        setDeleteGroupModal(false);
        setParentPortalGroupID(null);
        setReplacePortalDropdownNameError(false);
        setShowReplaceModal(true);
      }
    } catch (error: any) {
      console.log("Error:", error);
      setErrorMessage(
        extractErrorMessage(error, "Failed to validate group deletion"),
      );
    }
  };

  const handleRowsChange = (rows: number) => {
    setLimit(rows);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadPaginatedGroups({
      limit: limit,
      offset: (newPage - 1) * limit,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const setCurrentPortalGroup = (
    assignmentUid: string,
    isSubPortal?: boolean,
  ) => {
    if (assignmentUid) {
      if (isSubPortal) {
        for (const portalGroup of portalGroupsArray) {
          const seletedCourse = portalGroup.subGroups.find(
            (subGroup) => subGroup.PortalGroupUid === assignmentUid,
          );
          if (seletedCourse) {
            setSelectedPortalModal(seletedCourse);
            break;
          }
        }
      } else {
        const selectedCourse = portalGroupsArray.find(
          (portalGroup) => portalGroup.PortalGroupUid === assignmentUid,
        );
        if (selectedCourse) {
          setSelectedPortalModal(selectedCourse);
        }
      }
    }
  };

  useEffect(() => {
    if (openEditGroupModal && selectedCourseModal) {
      setName(selectedCourseModal.name);
      setThumbnailUrl(selectedCourseModal.thumbnail ?? null);
    }
  }, [openEditGroupModal, selectedCourseModal]);

  const handleConfirmDelete = async () => {
    try {
      setModalLoading(true);

      if (
        !portals.selectedPortal ||
        !auth.user?.UserPortalUid ||
        !selectedGroupIds.length
      ) {
        return;
      }

      await bulkDeletePortalGroups(portals.selectedPortal.portalUid, {
        groupIds: selectedGroupIds,
      });

      setSuccessMessage(
        selectedGroupIds.length === 1
          ? "Selected group was removed."
          : "Selected groups were removed.",
      );

      await loadPaginatedGroups();
      await loadShortGroups();
      await loadParentGroups();
      setSelectedPortalGroup([]);
      setParentPortalGroupID(null);
    } catch (error: any) {
      console.log("Error:", error);
      setErrorMessage(extractErrorMessage(error, "Failed to delete groups"));
    } finally {
      setDeleteGroupModal(false);
      setModalLoading(false);
    }
  };

  const handleReplaceConfirmDelete = async (replaceGroupId?: string) => {
    try {
      setModalLoading(true);

      if (
        !portals.selectedPortal ||
        !auth.user?.UserPortalUid ||
        !selectedGroupIds.length
      ) {
        return;
      }

      if (!replaceGroupId) {
        setReplacePortalDropdownNameError(true);
        return;
      }

      setReplacePortalDropdownNameError(false);

      await bulkDeletePortalGroups(portals.selectedPortal.portalUid, {
        groupIds: selectedGroupIds,
        replaceGroupId,
      });

      setSuccessMessage(
        selectedGroupIds.length === 1
          ? "Selected group was removed and replaced with the selected one."
          : "Selected groups were removed and replaced with the selected one.",
      );

      await loadPaginatedGroups();
      await loadShortGroups();
      await loadParentGroups();

      setShowReplaceModal(false);
      setSelectedPortalGroup([]);
    } catch (error: any) {
      console.log("Error:", error);
      setErrorMessage(
        extractErrorMessage(error, "Failed to replace and delete groups"),
      );
    } finally {
      setDeleteGroupModal(false);
      setModalLoading(false);
      setParentPortalGroupID(null);
    }
  };

  const handleReplaceConfirmDeleteModal = () => {
    const replaceGroupId = parentPortalGroupID ?? undefined;
    handleReplaceConfirmDelete(replaceGroupId);
  };

  const handleConfirmDeleteModal = () => {
    handleConfirmDelete();
  };

  const handleModalConfirm = () => {
    setModalLoading(false);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) {
      setNameError("");
    } else {
      setNameError("Portal name is required.");
    }
    setName(event.target.value);
  };

  const addNewGroup = async () => {
    try {
      if (!name.trim()) {
        setNameError("Portal name is required.");
        return;
      }
      if (!portals.selectedPortal) {
        return;
      }

      setButtonLoading(true);

      const body: portalGroupBody = {
        name,
        portalGroupId: openEditGroupModal
          ? selectedCourseModal?.PortalGroupUid
          : undefined,
        thumbnail: thumbnailURL ?? undefined,
        parentPortalGroupUid: parentPortalGroupID ?? undefined,
      };

      await createUpdatePortalGroup(portals?.selectedPortal.portalUid, body);
      setName("");
      setThumbnailUrl(null);
      setParentPortalGroupID(null);
      setOpenAddNewGroupModal(false);
      setOpenEditGroupModal(false);
      loadPaginatedGroups();
      loadShortGroups();
      loadParentGroups();
    } catch (error: any) {
      console.log("Error:", error);
      setErrorMessage(extractErrorMessage(error, "Failed to save group"));
    } finally {
      setButtonLoading(false);
    }
  };

  // Initial load and when limit/portal changes - load all data
  useEffect(() => {
    loadPaginatedGroups({
      limit: limit,
      offset: (page - 1) * limit,
    });
    loadShortGroups();
    loadParentGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, portals.selectedPortal]);

  // When search query changes - only reload table data
  useEffect(() => {
    loadPaginatedGroups({
      limit: limit,
      offset: (page - 1) * limit,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // if route contains assignment-search, use the current breadcrumbs, if not, use Portal configuration and group cards
  const breadcrumbs = useMemo(() => {
    if (location.pathname.includes("assignment-search")) {
      return [
        {
          label: "Assignment management",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Assignment search",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Group cards",
        },
      ];
    }
    return [
      {
        label: "Portal configuration",
        path: `/portal-configuration/${portalUid}`,
      },
      {
        label: "Group cards",
        path: `/portal-configuration/${portalUid}/group-cards`,
      },
    ];
  }, [portalUid, location.pathname]);

  usePageMetadata(
    {
      title: "Group cards",
      breadcrumbs,
    },
    [portalUid, portals.selectedPortal, breadcrumbs],
  );

  return (
    <>
      {errorMessage && (
        <Toast
          open={Boolean(errorMessage)}
          message={errorMessage}
          duration={2000}
          onClose={() => setErrorMessage("")}
          type={"error"}
        />
      )}
      {successMessage && (
        <Toast
          open={Boolean(successMessage)}
          message={successMessage}
          duration={2000}
          onClose={() => setSuccessMessage("")}
          type={"success"}
        />
      )}
      <Box>
        <DataTable
          data={portalGroupsArray}
          columns={[
            {
              id: "name",
              label: "Name",
              render: (row) => (
                <Typography
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenEditGroupModal(true);
                    setNameError("");
                    setCurrentPortalGroup(
                      row.PortalGroupUid,
                      !("subGroups" in row),
                    );
                  }}
                  sx={{
                    fontWeight: 600,
                    color: brand[600],
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {row.name}
                </Typography>
              ),
            },
          ]}
          totalCount={portalGroupResponse?.count || 0}
          page={page}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsChange}
          search={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by name"
          isLoading={loading}
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6">Group cards</Typography>
            </Box>
          }
          enableSelection={true}
          selectedCountTitle="Selected group cards"
          selectedRows={selectedPortalGroup}
          onSelectionChange={setSelectedPortalGroup}
          actions={
            <>
              {anySelected && (
                <>
                  <CustomActionIconLink
                    title="Remove"
                    icon={<DeleteOutlineIcon />}
                    onClick={handleBulkDeleteClick}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
              <Button
                sx={{
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  borderRadius: "12px",
                  padding: "16px",
                  "&:hover": {
                    bgcolor: `${brand[700]} !important`,
                  },
                }}
                onClick={() => {
                  setOpenAddNewGroupModal(true);
                  setName("");
                  setThumbnailUrl("");
                  setNameError("");
                }}
              >
                Add new group card
              </Button>
            </>
          }
          getRowId={(row) => row.PortalGroupUid}
          getNestedRows={(row) =>
            "subGroups" in row
              ? (row.subGroups as unknown as GetAllGroupsResult[])
              : undefined
          }
          nestedRowIndent={3}
        />
        <AddNewGroupModal
          name={name}
          nameError={nameError}
          openAddNewGroupModal={openAddNewGroupModal}
          setOpenAddNewGroupModal={setOpenAddNewGroupModal}
          modalTitle="Add new group card"
          fetchPortalGroups={onlyParentsGroupsArray}
          addNewGroup={addNewGroup}
          handleNameChange={handleNameChange}
          setParentPortalGroupID={setParentPortalGroupID}
          buttonLoading={buttonLoading}
          setThumbnailURL={setThumbnailUrl}
        />
        {deleteGroupModal && (
          <DeleteGroupModal
            openDeleteGroupModal={deleteGroupModal}
            setOpenDeleteGroupModal={setDeleteGroupModal}
            confirmButtonClick={handleConfirmDeleteModal}
            modalTitle={`${REMOVETEXT} Group${selectedGroupIds.length > 1 ? "s" : ""}`}
            modalSubtitle={`Are you sure you want to ${REMOVETEXT.toLowerCase()} ${
              selectedGroupIds.length > 1 ? "these groups" : "this group"
            }?`}
            handleModalConfirm={handleModalConfirm}
            modalLoading={modalLoading}
          />
        )}
        {showReplaceModal && (
          <ReplaceGroupModal
            openReplaceGroupModal={showReplaceModal}
            setOpenReplaceGroupModal={setShowReplaceModal}
            confirmButtonClick={handleReplaceConfirmDeleteModal}
            modalTitle={`Replace group${selectedGroupIds.length > 1 ? "s" : ""}`}
            handleModalConfirm={handleModalConfirm}
            modalLoading={modalLoading}
            fetchPortalGroups={nestedPortalGroupsArray}
            setParentPortalGroupID={setParentPortalGroupID}
            replacePortalDropdownNameError={replacePortalDropdownNameError}
            setReplacePortalDropdownNameError={
              setReplacePortalDropdownNameError
            }
          />
        )}

        {openEditGroupModal && selectedCourseModal && (
          <EditNewGroupModal
            name={name}
            nameError={nameError}
            openEditGroupModal={openEditGroupModal}
            setOpenEditGroupModal={setOpenEditGroupModal}
            modalTitle="Edit group"
            addNewGroup={addNewGroup}
            handleNameChange={handleNameChange}
            buttonLoading={buttonLoading}
            setThumbnailURL={setThumbnailUrl}
            selectedCourseModal={selectedCourseModal}
          />
        )}
      </Box>
    </>
  );
};
